import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { bookingModeState, shopState } from 'store/atoms';

import { RootLayout } from 'layouts/RootLayout';
import CardManagementPage from 'pages/CardManagementPage';
import CardRegisterRedirectPage from 'pages/CardRegisterRedirectPage';
import MainPage from 'pages/MainPage';

const BookingPage = lazy(() => import('pages/BookingPage'));
const CallgateBookingPage = lazy(() => import('pages/CallgateBookingPage'));
const CallgateInfoPage = lazy(() => import('pages/CallgateInfoPage'));
const MembershipPage = lazy(() => import('pages/MembershipPage'));
const PaymentPage = lazy(() => import('pages/PaymentPage'));
const PaymentCompletePage = lazy(() => import('pages/PaymentCompletePage'));

const MyBookingListPage = lazy(() => import('pages/My/MyBookingListPage'));
const MyBookingDetailPage = lazy(() => import('pages/My/MyBookingDetailPage'));
const MyMembershipListPage = lazy(() => import('pages/My/MyMembershipListPage'));
const MyMembershipDetailPage = lazy(() => import('pages/My/MyMembershipDetailPage'));
const MyRenewalListPage = lazy(() => import('pages/My/MyRenewalListPage'));
const MyRenewalDetailPage = lazy(() => import('pages/My/MyRenewalDetailPage'));

const EventListPage = lazy(() => import('pages/EventListPage'));
const EventDetailPage = lazy(() => import('pages/EventDetailPage'));

const OwnerShopFormPage = lazy(() => import('pages/OwnerShopFormPage'));

// 라우팅 변경을 감지하고 쿼리 파라미터를 자동으로 추가하는 컴포넌트
function RouterWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const shop = useRecoilValue(shopState);

  useEffect(() => {
    // shop 정보가 있고, 쿼리 파라미터에 shopName이 없는 경우 자동으로 추가
    // 단, shopId로 진입한 경우엔 자동 추가하지 않는다. 이전 매장명이 URL에 박혀
    // 초기화 직후 이전 매장을 재조회하는 레이스를 유발하기 때문.
    const urlParams = new URLSearchParams(location.search);
    const hasShopIdParam = urlParams.get('shopId');

    if (shop?.shopName && !hasShopIdParam) {
      const currentShopName = urlParams.get('shop');

      if (!currentShopName) {
        const newParams = new URLSearchParams(location.search);
        newParams.set('shop', shop.shopName);

        const newSearch = newParams.toString();
        const newUrl = `${location.pathname}?${newSearch}`;

        navigate(newUrl, { replace: true });
      }
    }
  }, [location.pathname, location.search, shop, navigate]);

  return <>{children}</>;
}

// 예약 모드에 따라 페이지 분기
// - callgate(shopId 진입, 국내): 홀수×인원 기반 시간 점유 모델 (앱 즉시확정과 동일)
// - global(shopName 진입): 기존 세션 기반 모델
function BookingRoute() {
  const bookingMode = useRecoilValue(bookingModeState);
  return bookingMode === 'callgate' ? <CallgateBookingPage /> : <BookingPage />;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RouterWrapper>
        <RootLayout />
      </RouterWrapper>
    ),
    children: [
      { index: true, element: <MainPage /> },
      { path: '/booking', element: <BookingRoute /> },
      // 전화예약매니저 ARS 가격·주차 문의 링크 — 콜게이트(shopId) 매장 가격표·주차 안내 + 하단 예약하기
      { path: '/info', element: <CallgateInfoPage /> },
      { path: '/membership', element: <MembershipPage /> },
      { path: '/payment', element: <PaymentPage /> },
      { path: '/complete', element: <PaymentCompletePage /> },
      { path: '/event', element: <EventListPage /> },
      { path: '/event/:id', element: <EventDetailPage /> },
      { path: '/card/register/:status', element: <CardRegisterRedirectPage /> },
      {
        path: 'my',
        children: [
          { path: 'booking', element: <MyBookingListPage /> },
          { path: 'booking/:id', element: <MyBookingDetailPage /> },
          { path: 'membership', element: <MyMembershipListPage /> },
          { path: 'membership/:id', element: <MyMembershipDetailPage /> },
          { path: 'membership/renewal', element: <MyRenewalListPage /> },
          { path: 'membership/renewal/:id', element: <MyRenewalDetailPage /> },
          { path: 'cards', element: <CardManagementPage /> },
        ],
      },
      // { path: '*', element: <NotFoundPage /> },
    ],
  },
  // 사장님 매장 정보 입력 폼 — 카카오톡 링크로 들어오는 비로그인 독립 화면.
  // 예약 사이트와 완전히 분리하려고 '/' 의 자식이 아니라 형제 라우트로 둔다.
  // → RootLayout(헤더·사이드메뉴·로그인폼·useAppInitialization)과
  //   RouterWrapper(URL에 ?shop= 자동 주입)를 타지 않는다.
  {
    path: '/owner/:shopId',
    element: (
      <Suspense fallback={null}>
        <OwnerShopFormPage />
      </Suspense>
    ),
  },
]);
